import { useEffect, useRef, useState } from 'react';
import ApplyLeave from './Modals/ApplyLeave';
import Modal from '../../components/modal';
import { LeaveService } from '../../services/leave';
import { UsersApi } from '../../services/users';
import { toast } from 'react-toastify';
import DocumentPreviewModal from '../../components/DocumentPreview';

const LeavesForApprovalTable = ({ data, loading, error, onAssignSubstitute, onViewDoc, onApproveReject }: any) => {
  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 10;
  const totalPages = Math.ceil(data.length / rowsPerPage);
  const paginatedData = data.slice((currentPage - 1) * rowsPerPage, currentPage * rowsPerPage);

  useEffect(() => {
    setCurrentPage(1); // Reset to first page if data changes
  }, [data]);

  return (
    <div className="w-full">
      {loading ? (
        <div className="text-center py-8">Loading...</div>
      ) : error ? (
        <div className="text-center text-red-500 py-8">{error}</div>
      ) : data.length === 0 ? (
        <div className="text-center py-8">No leaves for approval.</div>
      ) : (
        <>
          {/* Desktop Table */}
          <div className="hidden sm:block overflow-x-auto">
            <table className="min-w-full bg-white border border-gray-200 text-base rounded-lg">
              <thead>
                <tr className="bg-gray-50">
                  <th className="px-4 py-2 border-b font-semibold text-left">Employee</th>
                  <th className="px-4 py-2 border-b font-semibold text-left">Type</th>
                  <th className="px-4 py-2 border-b font-semibold text-left">From</th>
                  <th className="px-4 py-2 border-b font-semibold text-left">To</th>
                  <th className="px-4 py-2 border-b font-semibold text-left">Status</th>
                  <th className="px-4 py-2 border-b font-semibold text-left">Actions</th>
                </tr>
              </thead>
              <tbody>
                {paginatedData.map((item: any, idx: number) => (
                  <tr key={idx} className="hover:bg-gray-50 transition">
                    <td className="px-4 py-2 border-b min-w-[180px] align-middle">
                      <div>
                        <span className="font-semibold text-gray-800">{item.user.firstName} {item.user.lastName}</span>
                        <div className="text-xs text-gray-400">{item.user.designation}</div>
                      </div>
                    </td>
                    <td className="px-4 py-2 border-b">{item.leave?.multipleLeave?.[0]?.leavetype}</td>
                    <td className="px-4 py-2 border-b whitespace-nowrap">{item.leave?.multipleLeave?.[0]?.date}</td>
                    <td className="px-4 py-2 border-b whitespace-nowrap">{item.leave?.multipleLeave?.[item.leave?.multipleLeave?.length - 1]?.date}</td>
                    <td className="px-4 py-2 border-b">
                      <span className={`px-2 py-1 rounded text-xs font-medium ${item.status === 'approved' ? 'bg-green-50 text-green-700 border border-green-100' : item.status === 'rejected' ? 'bg-red-50 text-red-700 border border-red-100' : 'bg-yellow-50 text-yellow-700 border border-yellow-100'}`}>{item.status || item.leave?.status || '-'}</span>
                    </td>
                    <td className="px-4 py-2 border-b flex gap-2 items-center min-w-[220px]">
                      <button title="View Doc" className="px-3 py-1 rounded border border-blue-200 text-blue-700 bg-blue-50 hover:bg-blue-100 text-xs font-medium" onClick={() => onViewDoc(item.medicalCertificateUrl || item.leave?.medicalCertificateUrl || '')}>View</button>
                      <button title="Approve" className="px-3 py-1 rounded border border-green-200 text-green-700 bg-green-50 hover:bg-green-100 text-xs font-medium" onClick={() => onApproveReject(item, 'approved')}>Approve</button>
                      {(!item.leave?.substituteUserId) && (
                        <button title="Assign Substitute" className="px-3 py-1 rounded border border-purple-200 text-purple-700 bg-purple-50 hover:bg-purple-100 text-xs font-medium" onClick={() => onAssignSubstitute(item)}>Assign</button>
                      )}
                      <button title="Reject" className="px-3 py-1 rounded border border-red-200 text-red-700 bg-red-50 hover:bg-red-100 text-xs font-medium" onClick={() => onApproveReject(item, 'rejected')}>Reject</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {/* Pagination Controls */}
            {totalPages > 1 && (
              <div className="flex justify-center items-center gap-2 mt-4">
                <button
                  className="px-3 py-1 rounded border bg-gray-100 text-gray-700 disabled:opacity-50"
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                >
                  Prev
                </button>
                <span>Page {currentPage} of {totalPages}</span>
                <button
                  className="px-3 py-1 rounded border bg-gray-100 text-gray-700 disabled:opacity-50"
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                >
                  Next
                </button>
              </div>
            )}
          </div>
          {/* Mobile Card List */}
          <div className="block sm:hidden space-y-4">
            {paginatedData.map((item: any, idx: number) => (
              <div key={idx} className="bg-white rounded-lg shadow p-4 border border-gray-200">
                <div className="font-semibold text-gray-800 mb-1">{item.user.firstName} {item.user.lastName}</div>
                <div className="text-xs text-gray-400 mb-2">{item.user.designation}</div>
                <div className="mb-1"><b>Type:</b> {item.leave?.multipleLeave?.[0]?.leavetype}</div>
                <div className="mb-1"><b>From:</b> {item.leave?.multipleLeave?.[0]?.date}</div>
                <div className="mb-1"><b>To:</b> {item.leave?.multipleLeave?.[item.leave?.multipleLeave?.length - 1]?.date}</div>
                <div className="mb-1"><b>Status:</b> <span className={`px-2 py-1 rounded text-xs font-medium ${item.status === 'approved' ? 'bg-green-50 text-green-700 border border-green-100' : item.status === 'rejected' ? 'bg-red-50 text-red-700 border border-red-100' : 'bg-yellow-50 text-yellow-700 border border-yellow-100'}`}>{item.status || item.leave?.status || '-'}</span></div>
                <div className="flex gap-2 mt-2 flex-wrap">
                  <button className="px-3 py-1 rounded border border-blue-200 text-blue-700 bg-blue-50 hover:bg-blue-100 text-xs font-medium" onClick={() => onViewDoc(item.medicalCertificateUrl || item.leave?.medicalCertificateUrl || '')}>View</button>
                  <button className="px-3 py-1 rounded border border-green-200 text-green-700 bg-green-50 hover:bg-green-100 text-xs font-medium" onClick={() => onApproveReject(item, 'approved')}>Approve</button>
                  {(!item.leave?.substituteUserId) && (
                    <button className="px-3 py-1 rounded border border-purple-200 text-purple-700 bg-purple-50 hover:bg-purple-100 text-xs font-medium" onClick={() => onAssignSubstitute(item)}>Assign</button>
                  )}
                  <button className="px-3 py-1 rounded border border-red-200 text-red-700 bg-red-50 hover:bg-red-100 text-xs font-medium" onClick={() => onApproveReject(item, 'rejected')}>Reject</button>
                </div>
              </div>
            ))}
            {/* Pagination Controls for mobile */}
            {totalPages > 1 && (
              <div className="flex justify-center items-center gap-2 mt-4">
                <button
                  className="px-3 py-1 rounded border bg-gray-100 text-gray-700 disabled:opacity-50"
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                >
                  Prev
                </button>
                <span>Page {currentPage} of {totalPages}</span>
                <button
                  className="px-3 py-1 rounded border bg-gray-100 text-gray-700 disabled:opacity-50"
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                >
                  Next
                </button>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

const ApprovalHistoryTimeline = ({ history, onClose }: { history: any[]; onClose: () => void }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
    <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg mx-2 sm:mx-auto max-h-[90vh] flex flex-col">
      <h3 className="text-xl sm:text-2xl font-bold mb-4 text-center text-green-700 pt-6">Approval Timeline</h3>
      <div className="flex-1 overflow-y-auto px-2 sm:px-6 pb-4">
        <div className="relative flex flex-col items-center">
          <div className="absolute left-1/2 -translate-x-1/2 w-1 bg-green-300 h-full z-0" style={{ minHeight: '2.5rem' }}></div>
          {history && history.length > 0 ? (
            history.map((step, idx) => {
              const isLast = idx === history.length - 1;
              const actionColor = step.status === 'approved' ? 'bg-green-500' : step.status === 'pending' ? 'bg-yellow-400' : 'bg-red-500';
              const statusColor = step.status === 'approved' ? 'bg-green-100 text-green-700' : step.status === 'pending' ? 'bg-yellow-100 text-yellow-700' : 'bg-red-100 text-red-700';
              return (
                <div key={step.id || idx} className="relative flex flex-col items-center w-full mb-10 last:mb-0">
                  {/* Timeline dot */}
                  <div className={`absolute left-1/2 -translate-x-1/2 top-2 w-5 h-5 rounded-full border-4 border-white shadow ${actionColor} z-10`}></div>
                  {/* Timeline connector (skip for last) */}
                  {!isLast && <div className="absolute left-1/2 -translate-x-1/2 top-7 w-1 h-full bg-green-200 z-0" style={{ minHeight: '2.5rem' }}></div>}
                  <div className="relative bg-white rounded-xl shadow-md p-4 w-full max-w-xs sm:max-w-md mt-6 border border-gray-100">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-2 gap-2">
                      <span className="font-semibold text-base sm:text-lg text-green-800">Step {step.stepNumber}</span>
                      <span className={`text-xs px-3 py-1 rounded-full font-semibold capitalize ${statusColor}`}>{step.status}</span>
                    </div>
                    <div className="mb-1 text-sm sm:text-base"><span className="font-semibold">Approver:</span> {step.approver?.firstName} {step.approver?.lastName}</div>
                    <div className="mb-1 text-sm sm:text-base"><span className="font-semibold">Assign Date:</span> {step.actionDate ? new Date(step.actionDate).toLocaleString() : '-'}</div>
                    {step.statusUpdatedAt && (
                      <div className="mb-1 text-sm sm:text-base"><span className="font-semibold">Action Date:</span> {new Date(step.statusUpdatedAt).toLocaleString()}</div>
                    )}
                    <div className="mb-1 text-sm sm:text-base"><span className="font-semibold">Remarks:</span> {step.remarks || '-'}</div>
                  </div>
                </div>
              );
            })
          ) : (
            <div className="text-center text-gray-500">No approval history found.</div>
          )}
        </div>
      </div>
      <div className="sticky bottom-0 left-0 right-0 bg-white rounded-b-2xl pt-2 pb-6 flex justify-center z-20 border-t border-gray-100">
        <button className="bg-gradient-to-r from-green-500 to-green-700 text-white px-6 py-2 rounded-lg shadow hover:from-green-600 hover:to-green-800 transition-all font-semibold" onClick={onClose}>Close</button>
      </div>
    </div>
  </div>
);

const MyAppliedLeavesTable = ({ data, loading, error, onViewDoc, onForward, onApproveReject, onAssignSubstitute }: any) => {
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);
  const [selectedHistory, setSelectedHistory] = useState<any[]>([]);
  const [hierarchyMap, setHierarchyMap] = useState<{ [designation: string]: any[] }>({});
  const { fetchAllHierarchies } = LeaveService(); // <-- Move hook call to top level
  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 10;
  const totalPages = Math.ceil(data.length / rowsPerPage);
  const paginatedData = data.slice((currentPage - 1) * rowsPerPage, currentPage * rowsPerPage);

  useEffect(() => {
    setCurrentPage(1); // Reset to first page if data changes
  }, [data]);

  // Fetch all hierarchies once
  useEffect(() => {
    async function fetchHierarchies() {
      const res = await fetchAllHierarchies();
      const map: { [designation: string]: any[] } = {};
      (Array.isArray(res?.data) ? res.data : []).forEach((item: any) => {
        map[item.designation] = item.steps || [];
      });
      setHierarchyMap(map);
    }
    fetchHierarchies();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const canShowForward = (item: any) => {
    const approvalHistory = item.approvalHistory || [];
    if (!approvalHistory.length) return false;
    // Get user role from localStorage userDetails
    let userRole = '';
    try {
      const userDetails = JSON.parse(localStorage.getItem('userDetails') || '{}');
      userRole = userDetails.role || '';
    } catch (e) {
      userRole = '';
    }
    const hierarchySteps = hierarchyMap[userRole] || [];
    if (hierarchySteps.length === 0) return false;

    // If all steps are complete (history length >= hierarchy length), do not show Forward
    if (approvalHistory.length >= hierarchySteps.length) {
      return false;
    }

    // Otherwise, use your existing logic
    const lastHistoryStep = approvalHistory[approvalHistory.length - 1];
    return (
      lastHistoryStep.status === 'approved' &&
      lastHistoryStep.stepNumber < hierarchySteps.length
    );
  };

  // Find max stepNumber for each applyLeaveId
  const maxStepMap: Record<number, number> = {};
  data.forEach((item: any) => {
    const id = item.applyLeaveId;
    if (!maxStepMap[id] || item.stepNumber > maxStepMap[id]) {
      maxStepMap[id] = item.stepNumber;
    }
  });

  const openHistory = (history: any[]) => {
    setSelectedHistory(history);
    setIsHistoryOpen(true);
  };

  const closeHistory = () => {
    setIsHistoryOpen(false);
    setSelectedHistory([]);
  };

  return (
    <div>
      {loading ? (
        <div className="text-center py-8">Loading...</div>
      ) : error ? (
        <div className="text-center text-red-500 py-8">{error}</div>
      ) : data.length === 0 ? (
        <div className="text-center py-8">No applied leaves found.</div>
      ) : (
        <>
          {/* Table for desktop */}
          <div className="hidden sm:block overflow-x-auto">
            <table className="min-w-full bg-white border border-gray-200 text-base">
              <thead>
                <tr>
                  <th className="px-4 py-2 border-b">Type</th>
                  <th className="px-4 py-2 border-b">From</th>
                  <th className="px-4 py-2 border-b">To</th>
                  <th className="px-4 py-2 border-b">Status</th>
                  <th className="px-4 py-2 border-b">Actions</th>
                </tr>
              </thead>
              <tbody>
                {paginatedData.map((item: any, idx: number) => {
                  // For new response, approvalHistory is on the leave object
                  const approvalHistory = item.approvalHistory || [];
                  return (
                    <tr key={idx}>
                      <td className="px-4 py-2 border-b">{item.multipleLeave?.[0]?.leavetype}</td>
                      <td className="px-4 py-2 border-b">{item.multipleLeave?.[0]?.date}</td>
                      <td className="px-4 py-2 border-b">{item.multipleLeave?.[item.multipleLeave?.length - 1]?.date}</td>
                      <td className="px-4 py-2 border-b">
                        <button className="underline text-blue-600 hover:text-blue-800" onClick={() => openHistory(approvalHistory)}>
                          {approvalHistory.length > 0 ? approvalHistory[approvalHistory.length - 1].status : 'N/A'}
                        </button>
                      </td>
                      <td className="px-4 py-2 border-b flex gap-2 items-center">
                        <button title="View Doc" className="px-3 py-1 rounded border border-blue-200 text-blue-700 bg-blue-50 hover:bg-blue-100 text-xs font-medium" onClick={() => onViewDoc(item.medicalCertificateUrl || '')}>View</button>
                        {canShowForward(item) && (
                          <button
                            className="px-3 py-1 rounded border border-yellow-400 text-yellow-700 bg-yellow-50 hover:bg-yellow-100 text-xs font-medium"
                            onClick={() => onForward(item)}
                          >
                            Forward
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {/* Pagination Controls */}
            {totalPages > 1 && (
              <div className="flex justify-center items-center gap-2 mt-4">
                <button
                  className="px-3 py-1 rounded border bg-gray-100 text-gray-700 disabled:opacity-50"
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                >
                  Prev
                </button>
                <span>Page {currentPage} of {totalPages}</span>
                <button
                  className="px-3 py-1 rounded border bg-gray-100 text-gray-700 disabled:opacity-50"
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                >
                  Next
                </button>
              </div>
            )}
          </div>
          {/* Card layout for mobile */}
          <div className="sm:hidden flex flex-col gap-4">
            {paginatedData.map((item: any, idx: number) => {
              const approvalHistory = item.approvalHistory || [];
              return (
                <div key={idx} className="bg-white rounded-lg shadow p-4 border border-gray-200">
                  <div className="mb-2"><span className="font-semibold">Type:</span> {item.multipleLeave?.[0]?.leavetype}</div>
                  <div className="mb-2"><span className="font-semibold">From:</span> {item.multipleLeave?.[0]?.date}</div>
                  <div className="mb-2"><span className="font-semibold">To:</span> {item.multipleLeave?.[item.multipleLeave?.length - 1]?.date}</div>
                  <div className="mb-2"><span className="font-semibold">Status:</span> <button className="underline text-blue-600 hover:text-blue-800" onClick={() => openHistory(approvalHistory)}>{approvalHistory.length > 0 ? approvalHistory[approvalHistory.length - 1].status : 'N/A'}</button></div>
                  <div className="flex gap-2 mt-2 flex-wrap">
                    <button className="px-3 py-1 rounded border border-blue-200 text-blue-700 bg-blue-50 hover:bg-blue-100 text-xs font-medium" onClick={() => onViewDoc(item.medicalCertificateUrl || '')}>View</button>
                    {canShowForward(item) && (
                      <button
                        className="px-3 py-1 rounded border border-yellow-400 text-yellow-700 bg-yellow-50 hover:bg-yellow-100 text-xs font-medium"
                        onClick={() => onForward(item)}
                      >
                        Forward
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
            {/* Pagination Controls for mobile */}
            {totalPages > 1 && (
              <div className="flex justify-center items-center gap-2 mt-4">
                <button
                  className="px-3 py-1 rounded border bg-gray-100 text-gray-700 disabled:opacity-50"
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                >
                  Prev
                </button>
                <span>Page {currentPage} of {totalPages}</span>
                <button
                  className="px-3 py-1 rounded border bg-gray-100 text-gray-700 disabled:opacity-50"
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                >
                  Next
                </button>
              </div>
            )}
          </div>
          <Modal isOpen={isHistoryOpen} onClose={closeHistory}>
            <ApprovalHistoryTimeline history={selectedHistory} onClose={closeHistory} />
          </Modal>
        </>
      )}
    </div>
  );
};

const LeaveApprovalAndApply = () => {
  const [isApplyLeaveOpen, setApplyOpenLeave] = useState(false);
  const [activeTab, setActiveTab] = useState<'applied' | 'approval' | 'joining'>('applied');
  const [approvalLeaves, setApprovalLeaves] = useState<any[]>([]);
  const [appliedLeaves, setAppliedLeaves] = useState<any[]>([]);
  const [appliedMedicalLeaves, setAppliedMedicalLeaves] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { getPendingApprovals, getLeaveHistory, getNextStepApproversForLeave, createLeaveApprovalHistory, getMedicalLeave } = LeaveService();
  const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);
  const [allUsers, setAllUsers] = useState<any[]>([]);
  const [selectedUser, setSelectedUser] = useState('');
  const [assigningLeave, setAssigningLeave] = useState<any>(null);
  const { fetchAllUserForLeave } = UsersApi();
  const [userSearch, setUserSearch] = useState('');
  const [isDocModalOpen, setIsDocModalOpen] = useState(false);
  const [docUrl, setDocUrl] = useState('');
  const [isApproveRejectModalOpen, setIsApproveRejectModalOpen] = useState(false);
  const [approveRejectType, setApproveRejectType] = useState<'approved' | 'rejected' | null>(null);
  const [approveRejectItem, setApproveRejectItem] = useState<any>(null);
  const [approveRejectRemark, setApproveRejectRemark] = useState('');
  const { updateApprovalHistoryStatus, updateApplyLeaveSubstitute } = LeaveService();

  // Forward modal state
  const [isForwardModalOpen, setIsForwardModalOpen] = useState(false);
  const [forwardApprovers, setForwardApprovers] = useState<any[]>([]);
  const [selectedForwardApprover, setSelectedForwardApprover] = useState('');
  const [forwardLoading, setForwardLoading] = useState(false);
  const [forwardError, setForwardError] = useState<string | null>(null);
  const [forwardApplyLeaveId, setForwardApplyLeaveId] = useState<number | null>(null);
  const [forwardStepNumber, setForwardStepNumber] = useState<number | null>(null);

  // Forward button handler
  const handleForward = async (item: any) => {
    setForwardLoading(true);
    setForwardError(null);
    setIsForwardModalOpen(true);
    setForwardApplyLeaveId(item.id); // or item.applyLeaveId if that's correct
    try {
      const res = await getNextStepApproversForLeave(item.id); // or item.applyLeaveId
      setForwardApprovers(res?.data?.data || []);
      setForwardStepNumber(res?.data?.stepNumber || null); // store stepNumber from API
    } catch (err) {
      setForwardError('Failed to fetch next step approvers.');
      setForwardApprovers([]);
      setForwardStepNumber(null);
    } finally {
      setForwardLoading(false);
    }
  };

  // Forward submit handler
  const handleForwardSubmit = async () => {
    if (!forwardApplyLeaveId || !selectedForwardApprover || !forwardStepNumber) return;
    try {
      const res = await createLeaveApprovalHistory(forwardApplyLeaveId, forwardStepNumber, Number(selectedForwardApprover));
      if (res?.data?.status === 200) {
        toast.success('Leave forwarded successfully!');
        setIsForwardModalOpen(false);
        setSelectedForwardApprover('');
        setForwardApplyLeaveId(null);
        setForwardApprovers([]);
        setForwardStepNumber(null);
        fetchAppliedLeaves();
      } else {
        toast.error('Failed to forward leave.');
      }
    } catch (err) {
      toast.error('Failed to forward leave.');
    }
  };

  // Get userId if needed for API
  useEffect(() => {
    fetchApprovalLeaves();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (activeTab === 'approval') {
      fetchApprovalLeaves();
    } else if (activeTab === 'applied') {
      fetchAppliedLeaves();
    }
    else if (activeTab === 'joining') {
      fetchAppliedMedicalLeaves()
    }
    // eslint-disable-next-line
  }, [activeTab]);

  const fetchApprovalLeaves = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getPendingApprovals();
      setApprovalLeaves(res.data.data || []);
    } catch (err: any) {
      setError('Failed to fetch leaves for approval.');
    } finally {
      setLoading(false);
    }
  };

  const fetchAppliedLeaves = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getLeaveHistory();
      setAppliedLeaves(res.data.data || []);
    } catch (err: any) {
      setError('Failed to fetch applied leaves.');
    } finally {
      setLoading(false);
    }
  };

  const fetchAppliedMedicalLeaves = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getMedicalLeave();
      console.log("res", res);

      setAppliedMedicalLeaves(res.data.data || []);
    } catch (err: any) {
      setError('Failed to fetch applied leaves.');
    } finally {
      setLoading(false);
    }
  };

  const handleAssignSubstitute = async (leaveItem: any) => {
    setAssigningLeave(leaveItem);
    setIsAssignModalOpen(true);
    const users = await fetchAllUserForLeave();
    setAllUsers(users || []);
  };

  const handleAssignSubmit = async () => {
    if (!assigningLeave || !selectedUser) return;
    // Use the correct property for leave ID
    const leaveId = assigningLeave.applyLeaveId || assigningLeave.leave?.id || assigningLeave.id;
    try {
      const res = await updateApplyLeaveSubstitute(leaveId, Number(selectedUser));
      if (res?.data?.status === 200) {
        toast.success('Substitute assigned successfully!');
        setIsAssignModalOpen(false);
        setSelectedUser('');
        setAssigningLeave(null);
        fetchApprovalLeaves();
      } else {
        toast.error('Failed to assign substitute.');
      }
    } catch (err) {
      toast.error('Failed to assign substitute.');
    }
  };

  const handleApproveReject = (item: any, type: 'approved' | 'rejected') => {

    setApproveRejectItem(item);
    setApproveRejectType(type);
    setIsApproveRejectModalOpen(true);
    setApproveRejectRemark('');
  };

  const handleApproveRejectSubmit = async () => {
    if (!approveRejectItem || !approveRejectType) return;
    try {
      // Find approvalHistoryId (for demo, use item.id or item.approvalHistoryId)
      const approvalHistoryId = approveRejectItem.approvalHistory.id || approveRejectItem.approvalHistoryId;
      const res = await updateApprovalHistoryStatus(approvalHistoryId, approveRejectType, approveRejectRemark);
      if (res?.data?.status === 200) {
        toast.success('Leave status updated successfully!');
        setIsApproveRejectModalOpen(false);
        setApproveRejectRemark('');
        setApproveRejectItem(null);
        setApproveRejectType(null);
        // Refresh approval table
        fetchApprovalLeaves();
      } else {
        toast.error('Failed to update leave status.');
      }
    } catch (err: any) {
      toast.error('Failed to update leave status.');
    }
  };

  return (
    <div className="flex justify-center items-start min-h-[80vh] bg-gray-100 py-4 sm:py-8 px-2 sm:px-4 overflow-auto">
      <div className="relative w-full max-w-7xl bg-white rounded-xl shadow-lg p-2 sm:p-6 mx-auto overflow-auto">
        <div className="min-w-[800px]">
          <h2 className="text-lg sm:text-2xl font-bold mb-4 sm:mb-8 text-gray-800 text-center">
            Leave Approval & Apply
          </h2>

          <button
            className="absolute top-2 sm:top-6 right-2 sm:right-6 bg-green-600 text-white px-3 sm:px-6 py-1.5 sm:py-2 rounded shadow hover:bg-green-700 transition-all text-xs sm:text-base"
            onClick={() => setApplyOpenLeave(true)}
          >
            Apply Leave
          </button>

          <div className="flex border-b mb-4 sm:mb-6 bg-white rounded-t-lg overflow-x-auto">
            <button
              className={`px-4 py-2 font-semibold focus:outline-none border-b-2 transition-all duration-200 ${activeTab === 'applied' ? 'border-green-600 text-green-700 bg-green-50' : 'border-transparent text-gray-500 hover:bg-gray-50'}`}
              onClick={() => setActiveTab('applied')}
            >
              My Applied Leaves
            </button>
            <button
              className={`px-4 py-2 font-semibold focus:outline-none border-b-2 transition-all duration-200 ${activeTab === 'approval' ? 'border-green-600 text-green-700 bg-green-50' : 'border-transparent text-gray-500 hover:bg-gray-50'}`}
              onClick={() => setActiveTab('approval')}
            >
              Leaves For Approval
            </button>
            <button
              className={`px-4 py-2 font-semibold focus:outline-none border-b-2 transition-all duration-200 ${activeTab === 'joining' ? 'border-green-600 text-green-700 bg-green-50' : 'border-transparent text-gray-500 hover:bg-gray-50'}`}
              onClick={() => setActiveTab('joining')}
            >
              Joining
            </button>
          </div>

          {/* Scrollable main content area */}
          <div className="overflow-auto max-h-[70vh]">
            {activeTab === 'applied' && (
              <MyAppliedLeavesTable
                data={appliedLeaves}
                loading={loading}
                error={error}
                onViewDoc={(url: string) => {
                  setDocUrl(url);
                  setIsDocModalOpen(true);
                }}
                onForward={handleForward}
                onApproveReject={handleApproveReject}
                onAssignSubstitute={handleAssignSubstitute}
              />
            )}
            {activeTab === 'approval' && (
              <LeavesForApprovalTable
                data={approvalLeaves}
                loading={loading}
                error={error}
                onAssignSubstitute={handleAssignSubstitute}
                onViewDoc={(url: string) => {
                  setDocUrl(url);
                  setIsDocModalOpen(true);
                }}
                onApproveReject={handleApproveReject}
              />
            )}
            {activeTab === 'joining' && (
              <TbaJoiningTab
                leaveData={appliedMedicalLeaves}
                onUploadSuccess={fetchAppliedMedicalLeaves} // ✅ PASS CALLBACK
              // error={error}
              // onAssignSubstitute={handleAssignSubstitute}
              // onViewDoc={(url: string) => {
              //   setDocUrl(url);
              //   setIsDocModalOpen(true);
              // }}
              // onApproveReject={handleApproveReject}
              />
            )}
          </div>

          {/* Apply Leave Modal */}
          <Modal isOpen={isApplyLeaveOpen} onClose={() => setApplyOpenLeave(false)}>
            <ApplyLeave
              setApplyOpenLeave={setApplyOpenLeave}
              setBalancedLeave={() => { }}
              setleaveRequestData={() => { }}
            />
          </Modal>

          {/* Assign Substitute Modal */}
          <Modal isOpen={isAssignModalOpen} onClose={() => setIsAssignModalOpen(false)}>
            <div className="p-4 w-full max-w-xs sm:max-w-sm mx-auto bg-white rounded-xl shadow-2xl border border-gray-200">
              <h3 className="font-bold mb-4 text-center text-green-700">Assign Substitute</h3>
              <input
                type="text"
                className="w-full border rounded px-3 py-2 mb-3"
                placeholder="Search user..."
                value={userSearch}
                onChange={e => setUserSearch(e.target.value)}
              />
              <select
                className="w-full border rounded px-3 py-2 mb-4"
                value={selectedUser}
                onChange={e => setSelectedUser(e.target.value)}
              >
                <option value="">-- Select User --</option>
                {allUsers
                  .filter(user =>
                    `${user.firstName} ${user.lastName} ${user.email}`.toLowerCase().includes(userSearch.toLowerCase())
                  )
                  .map((user: any) => (
                    <option key={user.id} value={user.id}>
                      {user.firstName} {user.lastName} ({user.email})
                    </option>
                  ))}
              </select>
              <button
                className="bg-green-600 text-white px-4 py-2 rounded w-full"
                onClick={handleAssignSubmit}
                disabled={!selectedUser}
              >
                Assign
              </button>
            </div>
          </Modal>

          {/* Document View Modal */}
          <Modal isOpen={isDocModalOpen} onClose={() => setIsDocModalOpen(false)}>
            <div className="p-4 w-full max-w-lg mx-auto bg-white rounded-xl shadow-2xl border border-gray-200">
              <h3 className="font-bold mb-4 text-center text-green-700">Medical Certificate</h3>
              {docUrl ? (
                docUrl.toLowerCase().endsWith('.pdf') ? (
                  <iframe src={docUrl} title="Medical Certificate" className="w-full h-96" />
                ) : (
                  <img src={docUrl} alt="Medical Certificate" className="w-full max-h-96 object-contain mx-auto" />
                )
              ) : (
                <div className="text-center text-gray-500">No document available.</div>
              )}
            </div>
          </Modal>

          {/* Approve/Reject Modal */}
          <Modal isOpen={isApproveRejectModalOpen} onClose={() => setIsApproveRejectModalOpen(false)}>
            <div className="p-4 w-full max-w-xs sm:max-w-sm mx-auto bg-white rounded-xl shadow-2xl border border-gray-200">
              <h3 className="font-bold mb-4 text-center text-green-700">
                {approveRejectType === 'approved' ? 'Approve' : 'Reject'} Leave
              </h3>
              <textarea
                className="w-full border rounded px-3 py-2 mb-4"
                placeholder="Optional remark..."
                value={approveRejectRemark}
                onChange={e => setApproveRejectRemark(e.target.value)}
                rows={3}
              />
              <button
                className={`px-4 py-2 rounded w-full font-semibold ${approveRejectType === 'approved' ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'} text-white`}
                onClick={handleApproveRejectSubmit}
              >
                {approveRejectType === 'approved' ? 'Approve' : 'Reject'}
              </button>
            </div>
          </Modal>

          {/* Forward Modal */}
          <Modal isOpen={isForwardModalOpen} onClose={() => setIsForwardModalOpen(false)}>
            <div className="p-4 w-full max-w-xs sm:max-w-sm mx-auto bg-white rounded-xl shadow-2xl border border-gray-200">
              <h3 className="font-bold mb-4 text-center text-yellow-700">Forward Leave</h3>
              {forwardLoading ? (
                <div className="text-center py-4">Loading approvers...</div>
              ) : forwardError ? (
                <div className="text-center text-red-500 py-4">{forwardError}</div>
              ) : forwardApprovers.length === 0 ? (
                <div className="text-center text-gray-500">No further approvers. All steps are complete.</div>
              ) : (
                <>
                  <select
                    className="w-full border rounded px-3 py-2 mb-4"
                    value={selectedForwardApprover}
                    onChange={e => setSelectedForwardApprover(e.target.value)}
                  >
                    <option value="">-- Select Approver --</option>
                    {forwardApprovers.map((user: any) => (
                      <option key={user.id} value={user.id}>
                        {user.firstName} {user.lastName} ({user.designation})
                      </option>
                    ))}
                  </select>
                  <button
                    className="bg-yellow-600 text-white px-4 py-2 rounded w-full"
                    disabled={!selectedForwardApprover}
                    onClick={handleForwardSubmit}
                  >
                    Forward
                  </button>
                </>
              )}
            </div>
          </Modal>
        </div>
      </div>
    </div>

  );
};

const TbaJoiningTab = ({
  leaveData,
  onUploadSuccess
}: {
  leaveData: any[];
  onUploadSuccess: () => void;
}) => {
  const { uploadMedicalDoc } = LeaveService();

  const fileInputRefs = useRef<Record<number, HTMLInputElement | null>>({});

  const [isDocModalOpen, setIsDocModalOpen] = useState(false);
  const [docUrl, setDocUrl] = useState<string | null>(null);
  const [docType, setDocType] = useState<string | null>(null);

  const detectFileType = (url: string) => {
    if (url.endsWith(".pdf")) return "application/pdf";
    if (url.match(/\.(jpg|jpeg|png|gif)$/i)) return "image/*";
    return null;
  };

  const handleUpload = async (file: File | null, id: number, userId: number) => {
    if (!file) return;

    const formData = new FormData();
    formData.append("file", file);
    formData.append("id", String(id));
    formData.append("userId", String(userId));

    try {
      const res = await uploadMedicalDoc(formData);
      if (res.data.status === 200) {
        alert("File uploaded successfully");
        onUploadSuccess();
      }
    } catch (err) {
      console.error(err);
    }
  };

  const handleView = (fileUrl: string) => {
    setDocUrl(fileUrl);
    setDocType(detectFileType(fileUrl));
    setIsDocModalOpen(true);
  };

  const handleDelete = (id: number) => {
    console.log("Delete clicked for ID:", id);
  };

  return (
    <div className="p-4">
      <h2 className="text-lg font-semibold mb-4">TBA Joining - Leave Details</h2>

      <div className="overflow-x-auto">
        <table className="min-w-full border border-gray-300">
          <thead className="bg-gray-100">
            <tr>
              <th className="border px-4 py-2 text-center">Leave Name</th>
              <th className="border px-4 py-2 text-center">Start Date</th>
              <th className="border px-4 py-2 text-center">End Date</th>
              <th className="border px-4 py-2 text-center">Medical Certificate</th>
            </tr>
          </thead>

          <tbody>
            {leaveData.map((leave) => (
              <tr key={leave.id} className="hover:bg-gray-50">
                <td className="border px-4 py-2">{leave.leaveType}</td>
                <td className="border px-4 py-2">{leave.startDate}</td>
                <td className="border px-4 py-2">{leave.endDate}</td>

                <td className="border px-4 py-2">
                  {!leave.medicalCertificatePath ? (
                    <>
                      <input
                        type="file"
                        accept="application/pdf,image/*"
                        ref={(el) => (fileInputRefs.current[leave.id] = el)}
                        className="hidden"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) handleUpload(file, leave.id, leave.userId);
                        }}
                      />

                      <button
                        onClick={() => fileInputRefs.current[leave.id]?.click()}
                        className="bg-blue-500 text-white px-3 py-1 rounded text-sm hover:bg-blue-600"
                        style={{ minWidth: "110px" }}
                      >
                        Upload Doc
                      </button>
                    </>
                  ) : (
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleView(leave.medicalCertificatePath)}
                        className="bg-green-500 text-white px-3 py-1 rounded text-sm hover:bg-green-600"
                      >
                        View
                      </button>
                      <button
                        onClick={() => handleDelete(leave.id)}
                        className="bg-red-500 text-white px-3 py-1 rounded text-sm hover:bg-red-600"
                      >
                        Delete
                      </button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Document Preview Modal */}
      <DocumentPreviewModal
        isOpen={isDocModalOpen}
        onClose={() => setIsDocModalOpen(false)}
        fileUrl={docUrl}
        fileType={docType}
      />
    </div>
  );
};


export default LeaveApprovalAndApply; 